// Activity types for reminders and deal next steps (the "how"), and call outcomes.
// Goals spec Phase 0 (Brett, 2026-10-22). Calls and meetings count toward goals; emails don't
// (he decided emails would give a false sense of progress), so an email reminder just completes.

import { CalendarClock, Mail, Phone, type LucideIcon } from 'lucide-react';
import { addDaysISO } from './format';

export type ActivityType = 'call' | 'email' | 'meeting';

export const ACTIVITY: Record<ActivityType, { label: string; verb: string; icon: LucideIcon }> = {
  call: { label: 'Call', verb: 'Call', icon: Phone },
  email: { label: 'Email', verb: 'Email', icon: Mail },
  meeting: { label: 'Meeting', verb: 'Meet with', icon: CalendarClock },
};
export const ACTIVITY_TYPES = Object.keys(ACTIVITY) as ActivityType[];

export type Outcome = 'reached' | 'voicemail' | 'no_answer' | 'bad_number' | 'meeting_booked';

export const OUTCOMES: { key: Outcome; label: string }[] = [
  { key: 'reached', label: 'Reached them' },
  { key: 'voicemail', label: 'Left voicemail' },
  { key: 'no_answer', label: 'No answer' },
  { key: 'bad_number', label: 'Wrong / bad number' },
  { key: 'meeting_booked', label: 'Meeting booked' },
];
export const OUTCOME_LABEL = Object.fromEntries(OUTCOMES.map((o) => [o.key, o.label])) as Record<Outcome, string>;

/** The follow-up the app suggests after each outcome (Brett: always prompt for one). */
export function suggestedFollowUp(outcome: Outcome | null): { type: ActivityType; days: number } | null {
  switch (outcome) {
    case 'meeting_booked':
      return { type: 'meeting', days: 7 };
    case 'voicemail':
    case 'no_answer':
      return { type: 'call', days: 3 };
    case 'reached':
      return { type: 'call', days: 14 };
    case 'bad_number':
      return null;
    default:
      return { type: 'call', days: 7 };
  }
}

export const FOLLOW_UP_WHEN = [
  { label: 'Tomorrow', days: 1 },
  { label: '3 days', days: 3 },
  { label: '1 week', days: 7 },
  { label: '2 weeks', days: 14 },
  { label: '1 month', days: 30 },
].map((w) => ({ ...w, date: () => addDaysISO(w.days) }));

/** Default reminder text, e.g. "Call Eric Orozco". */
export const defaultNote = (type: ActivityType, who: string) => `${ACTIVITY[type].verb} ${who}`;
